import { setMachineDetailsData } from '../../../redux/machineDetailsDataSlice'
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { AddMachineNote } from '../machine_notes/addMachineNote';
import ArchiveMachineDialog from '../editMachine/archiveMachine'
import { Box, Typography, Menu, MenuItem } from '@mui/material';
import { ModifierLeNom } from '../editMachine/modifierLeNom';
import { DuplicateMachineDialoge } from './duplicateMachine';
import useResponsive from '../../../hooks/useResponsive';
import { useSelector, useDispatch } from 'react-redux';
import { DeleteRecord } from '../../deleteRecord';
import { useTranslation } from 'react-i18next';
import { useListBackNavigation } from '../../../hooks/listNavigation';
import { useState } from 'react';
import { usePdfPreview } from '../../../hooks/usePdfPreview';
import OpenPDFDialog from '../../openPDFDialog';
import MachineStatus from '../machineStatus';
import MachineType from '../machineType';
import Iconify from '../../Iconify';
import OpenPublicPageAction from '../../publicPage/OpenPublicPageAction';
import axios from 'axios';
import '../../../css/machines/machineDetailsHeader.css';

const MachineDetailsHeader = ({ selectedTab }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isDesktop = useResponsive('up', 'lg');

    const machineDetailsData = useSelector((state) => state.MachineDetailsData);
    const { goBack, goToList } = useListBackNavigation(
        machineDetailsData?.is_archived ? '/dashboard/machines_archivees' : '/dashboard/machines'
    );

    const hasMachineCreatePermission = useHasPermission('machine_create');
    const hasMachineUpdatePermission = useHasPermission('machine_update');
    const hasMachineDeletePermission = useHasPermission('machine_delete');
    const hasMachineArchivePermission = useHasPermission('machine_archive');
    const hasMachineUnarchivePermission = useHasPermission('machine_unarchive');
    const hasMachineQrCodePermission = useHasPermission('machine_generate_qr_code');

    const [errMsg, setErrMsg] = useState('');
    const [openAddNote, setOpenAddNote] = useState(false);
    const [openArchiveMachine, setOpenArchiveMachine] = useState(false);
    const [modifyTitre, setModifyTitre] = useState(false);
    const [deleteMachine, setDeleteMachine] = useState(false);
    const [openDuplicateMachine, setOpenDuplicateMachine] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const openActionMenu = Boolean(anchorEl);

    const {
        dialogProps,
        close: closePdfPreview,
        openLoadingPreview,
        openFromAxiosBlobResponse,
    } = usePdfPreview();

    const machineFileBaseName = (machineDetailsData?.serial_number || machineDetailsData?.id || 'machine')
        .replace(/[^a-zA-Z0-9-_]/g, '_');

    const handleDeleteMachine = async () => {
        try {
            const response = await axios.delete(`machine/${machineDetailsData?.id}`);
            if (response.status === 200 || response.status === 201) {
                setDeleteMachine(false);
                goToList();
                setErrMsg('');
            }
        } catch (error) {
            setErrMsg(error.message || 'Erreur lors de la suppression de la machine');
            console.error(error, 'Error deleting machine');
            CustomErrorLogger(error, 'Error deleting machine');
        }
    }

    const handleUnArchiveMachine = async () => {
        try {
            const response = await axios.patch(`machine/machine_unarchive/${machineDetailsData?.id}`);
            if (response.status === 200 || response.status === 201) {
                dispatch(setMachineDetailsData({ ...machineDetailsData, is_archived: false, archive_reason: null }));
            }
        } catch (error) {
            console.error(error, 'Error unarchiving machine');
            CustomErrorLogger(error, 'Error unarchiving machine');
        }
    };

    const handleOpenActionMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseActionMenu = () => {
        setAnchorEl(null);
    };

    const handleGoBack = goBack;

    const handleGetMachineQrCode = async () => {
        const fallbackFileName = `${machineFileBaseName}-qr-code.pdf`;
        try {
            openLoadingPreview({
                title: 'QR code machine',
                fileName: fallbackFileName,
            });
            const response = await axios.get(`machine-qr-code/${machineDetailsData?.id}`, { responseType: 'blob' });
            if (response.status === 200 || response.status === 201) {
                openFromAxiosBlobResponse(response, {
                    fallbackFileName,
                    title: 'QR code machine',
                });
            }
        } catch (error) {
            closePdfPreview();
            console.error(error, 'Error getting machine QR code');
            CustomErrorLogger(error, 'Error getting machine QR code');
        }
    };

    const handleGetMachineSecurityReport = async () => {
        const fallbackFileName = `${machineFileBaseName}-security-report.pdf`;
        try {
            openLoadingPreview({
                title: 'Fiche de Sécurité',
                fileName: fallbackFileName,
            });
            const response = await axios.get(`machine-pdf-security-report/${machineDetailsData?.id}`, { responseType: 'blob' });
            if (response.status === 200 || response.status === 201) {
                openFromAxiosBlobResponse(response, {
                    fallbackFileName,
                    title: 'Fiche de Sécurité',
                });
            }
        } catch (error) {
            closePdfPreview();
            console.error(error, 'Error getting machine security report');
            CustomErrorLogger(error, 'Error getting machine security report');
        }
    };

    const handleExportMachines = async () => {
        try {
            const response = await axios.get('machine/exportMachines', {
                params: { filters: { id: machineDetailsData?.id }, sortBy: {}, format: 'PDF' }, // Pass the format (Excel or PDF)
                responseType: 'blob', // Handle binary data
            });

            if (response.status === 200 || response.status === 201) {
                // Extract the filename from the Content-Disposition header
                const contentDisposition = response.headers['content-disposition'];
                let fileName = 'machines.pdf'; // Default filename
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="(.+)"/);
                    if (match && match[1]) {
                        fileName = match[1];
                    }
                }

                // Create a URL for the file
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName); // Use the extracted filename
                document.body.appendChild(link);
                link.click();

                // Clean up the URL object
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error exporting machines:', error);
            CustomErrorLogger(error, 'export machines in contracts page');
        }
    };

    return (
        <Box className="MachineDetailsHeader_mainBox">
            <Box className="MachineDetailsHeader_firstPart">
                <Iconify className='MachineDetailsHeader_back_icon' icon="material-symbols:arrow-back" onClick={handleGoBack} />
                <Typography className="MachineDetailsHeader_title">{machineDetailsData?.name}</Typography>
                {hasMachineUpdatePermission && !machineDetailsData?.is_archived && <Iconify className='MachineDetailsHeader_edit_icon' icon="ic:outline-edit" onClick={() => setModifyTitre(true)} />}
                {machineDetailsData?.archive_reason && <Typography className="MachineDetailsHeader_archive_reason">({t(machineDetailsData?.archive_reason)})</Typography>}
                <MachineStatus status={machineDetailsData?.status} />
                <MachineType type={machineDetailsData?.type} />
            </Box>
            <Box className="MachineDetailsHeader_secondPart">
                {isDesktop && !machineDetailsData?.is_archived &&
                    <Box className="MachineDetailsHeader_action_box" onClick={() => setOpenAddNote(true)}>
                        <Iconify className='MachineDetailsHeader_copy_icon' icon="simple-line-icons:note" />
                        <Typography className="MachineDetailsHeader_copy_title">{t('Remarque')}</Typography>
                    </Box>
                }
                {isDesktop && selectedTab === '1' && !machineDetailsData?.is_archived &&
                    <Box className="MachineDetailsHeader_action_box" onClick={() => handleExportMachines()}>
                        <Iconify className='MachineDetailsHeader_copy_icon' icon="material-symbols:file-export-outline-rounded" />
                        <Typography className="MachineDetailsHeader_copy_title">{t('Exporter')}</Typography>
                    </Box>
                }
                {isDesktop && hasMachineCreatePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                    <Box className="MachineDetailsHeader_action_box" onClick={() => setOpenDuplicateMachine(true)}>
                        <Iconify className='MachineDetailsHeader_copy_icon' icon="iconamoon:copy-bold" />
                        <Typography className="MachineDetailsHeader_copy_title">{t('Dupliquer')}</Typography>
                    </Box>
                }
                {isDesktop && hasMachineQrCodePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                    <Box className="MachineDetailsHeader_action_box" onClick={handleGetMachineQrCode}>
                        <Iconify className='MachineDetailsHeader_copy_icon' icon="ic:baseline-qrcode" />
                        <Typography className="MachineDetailsHeader_copy_title">{t('QRcode')}</Typography>
                    </Box>
                }
                {isDesktop && hasMachineQrCodePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                    <OpenPublicPageAction endpoint="machine-qr-code" entityId={machineDetailsData?.id} />
                }
                {isDesktop && hasMachineUpdatePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                    <Box className="MachineDetailsHeader_action_box" onClick={handleGetMachineSecurityReport}>
                        <Iconify className='MachineDetailsHeader_copy_icon' icon="carbon:report" />
                        <Typography className="MachineDetailsHeader_copy_title">{t('Fiche de Sécurité')}</Typography>
                    </Box>
                }
                {isDesktop && hasMachineArchivePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                    <Box className="MachineDetailsHeader_action_box" onClick={() => setOpenArchiveMachine(true)}>
                        <Iconify className='MachineDetailsHeader_copy_icon' icon="material-symbols:archive-outline" />
                        <Typography className="MachineDetailsHeader_copy_title">{t('Archiver')}</Typography>
                    </Box>
                }
                {isDesktop && hasMachineUnarchivePermission && selectedTab === '1' && machineDetailsData?.is_archived &&
                    <Box className="MachineDetailsHeader_action_box" onClick={handleUnArchiveMachine}>
                        <Iconify className='MachineDetailsHeader_copy_icon' icon="material-symbols:unarchive-outline" />
                        <Typography className="MachineDetailsHeader_copy_title">{t('Désarchiver')}</Typography>
                    </Box>
                }
                {isDesktop && hasMachineDeletePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                    <Box className="MachineDetailsHeader_action_box" onClick={() => setDeleteMachine(true)}>
                        <Iconify className='MachineDetailsHeader_delete_icon' icon="ic:outline-delete" />
                        <Typography className="MachineDetailsHeader_delete_title">{t('Supprimer')}</Typography>
                    </Box>
                }
                {!isDesktop && <Iconify
                    className='MachineDetailsHeader_dots_icon'
                    icon="vaadin:ellipsis-dots-v"
                    id="userPageActionMenuIcom"
                    aria-controls={openActionMenu ? 'long-menu' : undefined}
                    aria-expanded={openActionMenu ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleOpenActionMenu}
                />}
                <Menu
                    id="usersePageActionMenu"
                    MenuListProps="usersPageActionMenuIcom"
                    open={openActionMenu}
                    anchorEl={anchorEl}
                    onClose={handleCloseActionMenu}
                    onClick={handleCloseActionMenu}
                    slotProps={{ paper: { style: { width: '30ch', backgroundColor: '#fffff' } } }}
                >
                    {!isDesktop && !machineDetailsData?.is_archived &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={() => setOpenAddNote(true)}>
                            <Iconify className='MachineDetailsHeader_copy_icon' icon="simple-line-icons:note" />
                            <Typography className="MachineDetailsHeader_copy_title">{t('Remarque')}</Typography>
                        </MenuItem>
                    }
                    {!isDesktop && selectedTab === '1' && !machineDetailsData?.is_archived &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={() => handleExportMachines()}>
                            <Iconify className='MachineDetailsHeader_copy_icon' icon="material-symbols:file-export-outline-rounded" />
                            <Typography className="MachineDetailsHeader_copy_title">{t('Exporter')}</Typography>
                        </MenuItem>
                    }
                    {!isDesktop && hasMachineCreatePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={() => setOpenDuplicateMachine(true)}>
                            <Iconify className='MachineDetailsHeader_copy_icon' icon="iconamoon:copy-bold" />
                            <Typography className="MachineDetailsHeader_copy_title">{t('Dupliquer')}</Typography>
                        </MenuItem>
                    }
                    {!isDesktop && hasMachineQrCodePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={handleGetMachineQrCode}>
                            <Iconify className='MachineDetailsHeader_copy_icon' icon="ic:baseline-qrcode" />
                            <Typography className="MachineDetailsHeader_copy_title">{t('QRcode')}</Typography>
                        </MenuItem>
                    }
                    {!isDesktop && hasMachineQrCodePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                        <OpenPublicPageAction
                            asMenuItem
                            endpoint="machine-qr-code"
                            entityId={machineDetailsData?.id}
                        />
                    }
                    {!isDesktop && hasMachineUpdatePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={handleGetMachineSecurityReport}>
                            <Iconify className='MachineDetailsHeader_copy_icon' icon="carbon:report" />
                            <Typography className="MachineDetailsHeader_copy_title">{t('Fiche de Sécurité')}</Typography>
                        </MenuItem>
                    }
                    {!isDesktop && hasMachineCreatePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={() => setOpenArchiveMachine(true)}>
                            <Iconify className='MachineDetailsHeader_copy_icon' icon="material-symbols:archive-outline" />
                            <Typography className="MachineDetailsHeader_copy_title">{t('Archiver')}</Typography>
                        </MenuItem>
                    }
                    {!isDesktop && hasMachineUnarchivePermission && selectedTab === '1' && machineDetailsData?.is_archived &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={handleUnArchiveMachine}>
                            <Iconify className='MachineDetailsHeader_copy_icon' icon="material-symbols:unarchive-outline" />
                            <Typography className="MachineDetailsHeader_copy_title">{t('Désarchiver')}</Typography>
                        </MenuItem>
                    }
                    {!isDesktop && hasMachineDeletePermission && selectedTab === '1' && !machineDetailsData?.is_archived &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={() => setDeleteMachine(true)}>
                            <Iconify className='MachineDetailsHeader_delete_icon' icon="ic:outline-delete" />
                            <Typography className="MachineDetailsHeader_delete_title">{t('Supprimer')}</Typography>
                        </MenuItem>
                    }
                </Menu>
            </Box>

            {openAddNote &&
                <AddMachineNote
                    openAddNote={openAddNote}
                    setOpenAddNote={setOpenAddNote}
                />
            }

            {modifyTitre &&
                <ModifierLeNom
                    modifyTitre={modifyTitre}
                    setModifyTitre={setModifyTitre}
                />
            }

            {openDuplicateMachine &&
                <DuplicateMachineDialoge
                    defaultName={machineDetailsData?.name}
                    openDuplicateMachine={openDuplicateMachine}
                    setOpenDuplicateMachine={setOpenDuplicateMachine}
                />
            }

            {openArchiveMachine &&
                <ArchiveMachineDialog
                    machineDetailsData={machineDetailsData}
                    openArchiveMachine={openArchiveMachine}
                    setOpenArchiveMachine={setOpenArchiveMachine}
                />
            }

            {deleteMachine &&
                <DeleteRecord
                    errMsg={errMsg}
                    setErrMsg={setErrMsg}
                    openDelete={deleteMachine}
                    setOpenDelete={setDeleteMachine}
                    handleDelete={handleDeleteMachine}
                    title='Êtes-vous sûr de vouloir supprimer la machine ?'
                />
            }

            <OpenPDFDialog {...dialogProps} />
        </Box>
    );
}

export default MachineDetailsHeader;